import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useScreenTracking } from "./hooks/useScreenTracking";
import { SessionTimerProvider } from "./contexts/SessionTimerContext";
import { ChatProvider } from "./contexts/ChatContext";
import { BottomNavBar } from "./components/BottomNavBar";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NativeUpdateGate } from "./components/NativeUpdateGate";
import ZuzuRedesign from "./redesign/ZuzuRedesign";

// Every toast() caller in this app (SessionTimerBanner/Context, the legacy
// health pages, the redesign's signup/booking runtime) is itself already
// behind its own lazy boundary and only ever calls toast() in response to a
// user interaction - never during first mount. So the toast UI can be its
// own parallel chunk (out of the main bundle's parse cost, Lighthouse
// "reduce unused JavaScript") without any real risk of a dropped
// notification: it still starts fetching immediately on app load, just as a
// separate small request instead of inlined into the main bundle, and it has
// arrived long before any interaction-driven toast() call could plausibly
// fire.
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
// Google Consent Mode's "update" half (see index.html for the "default"
// half) — web only, since Consent Mode governs the GTM/GA4 web tags, not
// native Firebase Analytics.
const ConsentBanner = lazy(() =>
  import("@/components/ConsentBanner").then((m) => ({
    default: m.ConsentBanner,
  })),
);
// Never rendered on a redesign route (see LegacyChrome below) — it's only
// eagerly imported once instead of shipping the full Postgrest/Realtime/
// Storage Supabase client (see authClient.ts) to every marketing-page visit.
const HealthChatWidgetWrapper = lazy(() =>
  import("./components/HealthChatWidgetWrapper").then((m) => ({
    default: m.HealthChatWidgetWrapper,
  })),
);

// Every non-redesign page is code-split: eagerly importing them shipped the
// whole legacy app (admin, health suite, leaderboard, ...) to every marketing
// visitor as unused JavaScript. Each route loads on demand instead.
const lazyPage = <T extends React.ComponentType<never>>(
  load: () => Promise<{ default: T }>,
) => lazy(load);
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const Admin = lazyPage(() => import("./pages/Admin"));
const ScanIn = lazyPage(() => import("./pages/ScanIn"));
const FitnessReset = lazyPage(() => import("./pages/FitnessReset"));
const Leaderboard = lazyPage(() => import("./pages/Leaderboard"));
const Terms = lazyPage(() => import("./pages/Terms"));
const GuestTerms = lazyPage(() => import("./pages/GuestTerms"));
const HealthSettings = lazyPage(() => import("./pages/HealthSettings"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const Longevity = lazyPage(() => import("./pages/health/Longevity"));
const HealthPage = lazyPage(() => import("./pages/health/Health"));
const FitnessPage = lazyPage(() => import("./pages/health/Fitness"));
const SleepPage = lazyPage(() => import("./pages/health/Sleep"));
const InBodyPage = lazyPage(() => import("./pages/health/InBody"));
const Goals = lazyPage(() => import("./pages/Goals"));
const NutritionPage = lazyPage(() => import("./pages/health/Nutrition"));
const TestResultsPage = lazyPage(() => import("./pages/health/TestResults"));
const PaymentReturn = lazyPage(() => import("./pages/PaymentReturn"));
const PaymentCancel = lazyPage(() => import("./pages/PaymentCancel"));
const DoorDisplay = lazyPage(() => import("./pages/DoorDisplay"));
const InBodyRoomScreen = lazyPage(() => import("./pages/InBodyRoomScreen"));
const CleanerPin = lazyPage(() => import("./pages/CleanerPin"));
const CleaningRound = lazyPage(() => import("./pages/CleaningRound"));
const GroupBookingPage = lazyPage(() => import("./pages/GroupBookingPage"));
const Redeem = lazyPage(() => import("./pages/Redeem"));
const Founding = lazyPage(() => import("./pages/Founding"));

const queryClient = new QueryClient();

// On the native app, launch straight into the app (login gate) instead of the
// marketing home — the website opens at "/" but the app should start at the
// dashboard, which shows the sign-in screen when there's no session.
function NativeStartRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  // Report a descriptive screen name to Firebase on every native route change.
  useScreenTracking();
  useEffect(() => {
    if (Capacitor.isNativePlatform() && location.pathname === "/") {
      navigate("/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);
  return null;
}

// Paths owned by the ZUZU redesign — the design ships its own nav + chat, so
// the legacy bottom-nav and chat widget are suppressed on these routes.
const REDESIGN_PREFIXES = [
  "/about",
  "/book",
  "/pricing",
  "/equipment",
  "/experience",
  "/signup",
  "/contact",
  "/visit",
  "/legal",
  "/corporate-team-building",
  "/corporate-gift-cards",
  "/redeem",
  "/founding",
  "/birthday-party",
  "/bachelorette-party",
  "/team-recovery",
  "/date-night",
  "/gift-a-session",
  "/group-day-out",
  "/run-club-recovery",
  "/marathon-recovery",
  "/hyrox-recovery",
  "/crossfit-recovery",
  "/app",
  "/dashboard",
  "/auth",
  "/bookings",
  "/my-bookings",
  "/session",
  "/wallet",
  "/plans",
  "/profile",
];
// Full-screen wall/tablet pages — no member nav, no chat widget.
const KIOSK_PATHS = [
  "/door",
  "/frontdoor",
  "/cleaning-pin",
  "/cleaning-round",
  "/room/inbody",
];
const LegacyChrome = () => {
  const { pathname } = useLocation();
  const isRedesign =
    pathname === "/" ||
    REDESIGN_PREFIXES.some(
      (p) => pathname === p || pathname.startsWith(p + "/"),
    );
  if (isRedesign || KIOSK_PATHS.includes(pathname)) return null;
  return (
    <>
      <BottomNavBar />
      <Suspense fallback={null}>
        <HealthChatWidgetWrapper />
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SessionTimerProvider>
      <ChatProvider>
        <TooltipProvider>
          {/* The top session-timer bar is gone on purpose: the Today screen's
              dark session view is the one live-session surface (Ryan, 2026-08). */}
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
            {!Capacitor.isNativePlatform() && <ConsentBanner />}
          </Suspense>
          {/* A store build older than the site allows stops here until it is
              updated (store releases are manual, the site is not). */}
          {Capacitor.isNativePlatform() && <NativeUpdateGate />}
          <BrowserRouter>
            <NativeStartRedirect />
            <ErrorBoundary>
              <Suspense fallback={null}>
                <Routes>
                  {/* ── ZUZU redesign · marketing site ── */}
                  <Route
                    path="/"
                    element={<ZuzuRedesign view="site" page="home" />}
                  />
                  <Route
                    path="/about"
                    element={<ZuzuRedesign view="site" page="about" />}
                  />
                  {/* Public availability + slot picker — see what's open
                      before creating an account. */}
                  <Route
                    path="/book"
                    element={<ZuzuRedesign view="site" page="book" />}
                  />
                  <Route
                    path="/pricing"
                    element={<ZuzuRedesign view="site" page="pricing" />}
                  />
                  <Route
                    path="/equipment"
                    element={<ZuzuRedesign view="site" page="modalities" />}
                  />
                  <Route
                    path="/experience/plunge"
                    element={
                      <ZuzuRedesign view="site" page="modality:plunge" />
                    }
                  />
                  <Route
                    path="/experience/sauna"
                    element={<ZuzuRedesign view="site" page="modality:sauna" />}
                  />
                  <Route
                    path="/experience/compress"
                    element={<ZuzuRedesign view="site" page="modality:comp" />}
                  />
                  <Route
                    path="/experience/scan"
                    element={
                      <ZuzuRedesign view="site" page="modality:inbody" />
                    }
                  />
                  <Route
                    path="/experience/redlight"
                    element={<ZuzuRedesign view="site" page="modality:red" />}
                  />
                  <Route
                    path="/signup/membership"
                    element={<ZuzuRedesign view="site" page="signup:monthly" />}
                  />
                  <Route
                    path="/signup/credits"
                    element={<ZuzuRedesign view="site" page="signup:drop" />}
                  />
                  <Route
                    path="/signup/private"
                    element={<ZuzuRedesign view="site" page="signup:private" />}
                  />
                  <Route
                    path="/contact"
                    element={<ZuzuRedesign view="site" page="contact" />}
                  />
                  {/* /visit is a legacy alias for /contact — redirect rather
                  than render a second copy of the same page, which would
                  otherwise read to Google as duplicate content. */}
                  <Route
                    path="/visit"
                    element={<Navigate to="/contact" replace />}
                  />
                  <Route
                    path="/legal"
                    element={<ZuzuRedesign view="site" page="legal" />}
                  />
                  <Route
                    path="/corporate-team-building"
                    element={
                      <GroupBookingPage slug="corporate-team-building" />
                    }
                  />
                  <Route
                    path="/birthday-party"
                    element={<GroupBookingPage slug="birthday-party" />}
                  />
                  <Route
                    path="/bachelorette-party"
                    element={<GroupBookingPage slug="bachelorette-party" />}
                  />
                  <Route
                    path="/team-recovery"
                    element={<GroupBookingPage slug="team-recovery" />}
                  />
                  <Route
                    path="/date-night"
                    element={<GroupBookingPage slug="date-night" />}
                  />
                  <Route
                    path="/gift-a-session"
                    element={<GroupBookingPage slug="gift-a-session" />}
                  />
                  <Route
                    path="/corporate-gift-cards"
                    element={<GroupBookingPage slug="corporate-gift-cards" />}
                  />
                  {/* Gift-code redemption on the web — the other half of the
                      two routes the gift email offers. */}
                  <Route path="/redeem" element={<Redeem />} />
                  {/* Founding membership — the link shared with founding
                      members (unlimited anytime, R999 a month). */}
                  <Route path="/founding" element={<Founding />} />
                  <Route
                    path="/group-day-out"
                    element={<GroupBookingPage slug="group-day-out" />}
                  />
                  <Route
                    path="/run-club-recovery"
                    element={<GroupBookingPage slug="run-club-recovery" />}
                  />
                  <Route
                    path="/marathon-recovery"
                    element={<GroupBookingPage slug="marathon-recovery" />}
                  />
                  <Route
                    path="/hyrox-recovery"
                    element={<GroupBookingPage slug="hyrox-recovery" />}
                  />
                  <Route
                    path="/crossfit-recovery"
                    element={<GroupBookingPage slug="crossfit-recovery" />}
                  />

                  {/* ── ZUZU redesign · booking app (real-data wiring pending) ── */}
                  <Route
                    path="/app"
                    element={<ZuzuRedesign view="app" appScreen="today" />}
                  />
                  <Route
                    path="/dashboard"
                    element={<ZuzuRedesign view="app" appScreen="today" />}
                  />
                  <Route
                    path="/app/equipment"
                    element={<ZuzuRedesign view="app" appScreen="browse" />}
                  />
                  <Route
                    path="/auth"
                    element={<ZuzuRedesign view="app" appScreen="auth" />}
                  />
                  {/* "you signed up but never confirmed your email" — the
                      resend screen, reachable directly so a failed sign-in
                      (and the post-signup confirm screen) can link to it. */}
                  <Route
                    path="/auth/verify"
                    element={
                      <ZuzuRedesign view="app" appScreen="verifyEmail" />
                    }
                  />
                  {/* Password reset, linkable from outside the sign-in sheet. */}
                  <Route
                    path="/auth/forgot"
                    element={
                      <ZuzuRedesign view="app" appScreen="forgotPassword" />
                    }
                  />
                  <Route
                    path="/bookings"
                    element={<ZuzuRedesign view="app" appScreen="stack" />}
                  />
                  <Route
                    path="/my-bookings"
                    element={<ZuzuRedesign view="app" appScreen="bookings" />}
                  />
                  <Route
                    path="/session"
                    element={<ZuzuRedesign view="app" appScreen="session" />}
                  />
                  <Route
                    path="/wallet"
                    element={<ZuzuRedesign view="app" appScreen="wallet" />}
                  />
                  <Route
                    path="/plans"
                    element={<ZuzuRedesign view="app" appScreen="plans" />}
                  />
                  <Route
                    path="/profile"
                    element={<ZuzuRedesign view="app" appScreen="profile" />}
                  />

                  {/* ── Pages pending redesign restyle ── */}
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/scan" element={<ScanIn />} />
                  <Route path="/body" element={<InBodyPage />} />
                  <Route path="/body/longevity" element={<Longevity />} />
                  <Route path="/body/health" element={<HealthPage />} />
                  <Route path="/body/fitness" element={<FitnessPage />} />
                  <Route path="/body/sleep" element={<SleepPage />} />
                  <Route path="/body/inbody" element={<InBodyPage />} />
                  <Route path="/goals" element={<Goals />} />
                  <Route path="/body/nutrition" element={<NutritionPage />} />
                  <Route
                    path="/body/test-results"
                    element={<TestResultsPage />}
                  />
                  <Route path="/fitness-reset" element={<FitnessReset />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/guest-terms/:token" element={<GuestTerms />} />
                  <Route path="/health-settings" element={<HealthSettings />} />
                  <Route path="/payment/return" element={<PaymentReturn />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  {/* /door: the booking calendar, as the app opens it.
                      /frontdoor: the wall tablet at the door — the same
                      calendar, plus the welcome screen for a private hire. */}
                  <Route path="/door" element={<DoorDisplay />} />
                  <Route
                    path="/frontdoor"
                    element={<DoorDisplay wallDisplay />}
                  />
                  <Route path="/room/inbody" element={<InBodyRoomScreen />} />
                  <Route path="/cleaning-pin" element={<CleanerPin />} />
                  <Route path="/cleaning-round" element={<CleaningRound />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <LegacyChrome />
          </BrowserRouter>
        </TooltipProvider>
      </ChatProvider>
    </SessionTimerProvider>
  </QueryClientProvider>
);

export default App;
